import EXIF from "exif-js";

import { CALL_DATA, COMPRESSION_TYPE, GLOBAL_MEETING_DATA, IMAGE_COMPRESSION, IMAGE_SIZE_KEYS, PublicVariables } from "../constants/PublicVariables";
import {
    FILE_UPLOAD_LIMITATION,
    SELECTED_SCREEN,
    SUB_SCREEN,
    SUPPORTED_IMAGE_TYPES,
    UPLOAD_FILES_DATA,
    UPLOAD_FILE_DATA_TRACK_STATUS,
    UPLOAD_TYPE,
    UPLOAD_TYPE_PARAM_FOR_UPLOAD_FILES,
} from "../constants/UploadFilesConstants";
import { FIREBASE_EVENT_CATEGORY, FIREBASE_LOG_KEY, FIREBASE_LOG_VALUE } from "../constants/FirebaseConstants";
import { EVENT_NAME } from "../constants/GTagConstants";
import Localize from "../locale/Localize";

import reduxStore from "../configureEnvStore";

import { bytesToMB } from "../shared/function";
import CustomToast from "../components/UI/customToast";
import { LogFirebase } from "./firestore";
import googleEvent from "./googleEvent";
import {
    changeIsFromMarkupValue,
    changePreviousScreen,
    changeUploadFileScreen,
    changeUploadFileSubScreen,
    setIsHideFinalAddBtn,
    setUploadFileContainerImageArray,
    setUplodedFilesThumbListArray,
    updateImagesArray,
} from "../reducers/uploadFile-reducer";
import livePointerService from "../components/LivePointer/livePointerService";
import { NOTIFICATION_TYPES } from "../constants/DataTrackNotificationConstants";
import { sendDataTrack } from "../shared/dataTrackFunction";
import { setCurrentCallBodyScreen } from "../reducers/common-reducer";
import { CALL_BODY_SCREEN, MAX_CHUNK_SIZE } from "../constants/Constants";
import { dualStreamEnabled, isRemainingFileCountLess } from "../shared/utility";
import takePhotoService from "./takePhotoService";
import { isLocalUserStreaming } from "../components/Twilio/Room/roomFunction";
import { performStartStopVideo } from "../components/Twilio/VideoCall/VideoUtils";
import { getPublishVideoContainer, removeVideoElement } from "../components/Twilio/twilioCallUtility";
import { attachVideoTracksStreamingIdentityVise } from "../components/GalleryView/GalleryViewUtils";
import zoomService from "./zoomService";

class UploadFileService {
    openFileSelectDialog = false;
    isSizeExceed = false;

    getMaxAllowedFileSize() {
        return IMAGE_COMPRESSION.MAX_IMAGE_SIZE || FILE_UPLOAD_LIMITATION.IMAGE_SIZE_LIMITATION_IN_MB;
    }

    isImageLargerThanAllowedSizeLimit(imageSizeInBytes, sizeLimit = 0) {
        if (imageSizeInBytes === 0) return false;

        const allowedMaxSizeInMB = sizeLimit || this.getMaxAllowedFileSize();
        const fileSizeInMB = bytesToMB(imageSizeInBytes);

        return fileSizeInMB > allowedMaxSizeInMB;
    }

    transformImageSize = (width, height, maxSize) => {
        const ratio = maxSize / Math.max(width, height);
        const transform_width = Math.max(Math.round(width * ratio), 1);
        const transform_height = Math.max(Math.round(height * ratio), 1);
        return {
            w: transform_width,
            h: transform_height,
        };
    };

    formatBytes = (bytes, decimals = 2) => {
        if (bytes === 0) return "0 Bytes";
        const k = 1024;
        const dm = decimals < 0 ? 0 : decimals;
        const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
    };

    showFileMAXSizeMsg = () => {
        const localisedValues = Localize[PublicVariables.currentLanguageObj.languageCode];
        const fileRemovedMessage = localisedValues?.removed_more_than_20MB_file?.replace("<fileSize>", this.getMaxAllowedFileSize());
        LogFirebase(FIREBASE_EVENT_CATEGORY.UploadFiles, FIREBASE_LOG_KEY.FromDevice, FIREBASE_LOG_VALUE.RemovedLargeFiles);
        CustomToast.info(fileRemovedMessage);
    };

    generateFilesArray = async (files, updatedSelectedFiles, isCameraCapture = false) => {
        this.isSizeExceed = false;
        /* Remove files those are not images */
        let filteredLimitedSizeFiles = [];
        if (isCameraCapture) {
            filteredLimitedSizeFiles = files;
        } else {
            const filteredFiles = files.filter((file) => SUPPORTED_IMAGE_TYPES.includes(file.type));

            filteredLimitedSizeFiles = filteredFiles.filter((file) => !this.isImageLargerThanAllowedSizeLimit(file.size));

            if (filteredFiles.length === 0) {
                const localisedValues = Localize[PublicVariables.currentLanguageObj.languageCode];
                CustomToast.info(localisedValues?.unsupported_file_format);
                return;
            }

            if (filteredLimitedSizeFiles.length === 0 && filteredFiles.length !== 0) {
                this.isSizeExceed = true;
                this.showFileMAXSizeMsg();
                return;
            }

            if (filteredLimitedSizeFiles.length != filteredFiles.length) {
                this.isSizeExceed = true;
                this.showFileMAXSizeMsg();
            }
        }

        updatedSelectedFiles(filteredLimitedSizeFiles);
        reduxStore.dispatch(changeUploadFileSubScreen(SUB_SCREEN.MARKUP_LOADING_IMAGE));
        reduxStore.dispatch(changeUploadFileScreen(SELECTED_SCREEN.MARKUP));

        if (isCameraCapture && reduxStore.getState().uploadFile.previousScreen === SELECTED_SCREEN.MARKUP_WITH_DONE) {
            reduxStore.dispatch(changePreviousScreen(SELECTED_SCREEN.FINAL_UPLODING_VIEW));
        }
    };

    /*
    Get Image latitude & longitude of image if exist
    */
    getImageExifData = async (image) => {
        return new Promise((resolve) => {
            EXIF.getData(image, () => {
                const gpsLatitude = EXIF.getTag(image, "GPSLatitude");
                const gpsLongitude = EXIF.getTag(image, "GPSLongitude");
                if (gpsLatitude && gpsLongitude) {
                    const latitudeRef = EXIF.getTag(image, "GPSLatitudeRef") || "N";
                    const longitudeRef = EXIF.getTag(image, "GPSLongitudeRef") || "W";
                    const latitude = this.convertDMSToDD(gpsLatitude, latitudeRef);
                    const longitude = this.convertDMSToDD(gpsLongitude, longitudeRef);
                    resolve({ latitude, longitude });
                } else {
                    resolve(null); //Return null value if no latitude-longitude available for image
                }
            });
        });
    };

    convertDMSToDD = (coordinates, direction) => {
        const deg = coordinates[0];
        const min = coordinates[1];
        const sec = coordinates[2];
        const dd = deg + min / 60 + sec / (60 * 60);
        return direction === "S" || direction === "W" ? -dd : dd;
    };

    showUploadFilesView = (uploadFileType, toastMessage) => {
        if (isRemainingFileCountLess()) {
            if (PublicVariables.isMobile) {
                CustomToast.info(toastMessage);
            }
            return;
        }

        reduxStore.dispatch(setCurrentCallBodyScreen(CALL_BODY_SCREEN.UPLOAD_FILE_SCREEN));
        reduxStore.dispatch(changeUploadFileScreen(SELECTED_SCREEN.SELECT_FILES));

        CALL_DATA.myscreenShareON = reduxStore.getState().twilioEvents.isLocalUserScreenSharing;
        CALL_DATA.myVideoON = reduxStore.getState().twilioEvents.startLocalVideo;

        this.onStartUploadFiles(GLOBAL_MEETING_DATA.markupLiveStream);

        livePointerService.livePointerOnMarkupOCR(); // case -2 pointer is ON and user clicks on upload files

        /* 
            case - 1  own video in ON user clicks on upload files 
            Check for local user streaming, dual streaming    
        */
        if (isLocalUserStreaming()) {
            //do not unpublish if in multi stream
            if (!dualStreamEnabled()) {
                // to unpublish video before starting markup to save bandwidth
                if (CALL_DATA.myVideoON) {
                    performStartStopVideo();
                }
            }
        }

        if (uploadFileType === UPLOAD_TYPE.UPLOAD_ONLY) {
            /* Do nothing */
        } else if (uploadFileType !== UPLOAD_TYPE.EMPTY) {
            this.addFileSelectEvent();
            this.uploadFiles(uploadFileType);
        } else {
            googleEvent.logEvent(EVENT_NAME.UPLOAD_FILE_TAPPED);
        }

        /*
            Show/Hide divs of Bookmark, note, Participant & chat
            $scope.toggleDivById()
        */
    };

    addFileSelectEvent = () => {
        if (!(window.File && window.FileList && window.FileReader)) {
            LogFirebase(FIREBASE_EVENT_CATEGORY.UploadFiles, FIREBASE_LOG_KEY.WindowFile, FIREBASE_LOG_VALUE.FileAPINotSupported);
        }
    };

    uploadFiles = (uploadFileType) => {
        let firebaseLogValue = "";

        if (uploadFileType === UPLOAD_TYPE.FROM_CAMERA) {
            firebaseLogValue = FIREBASE_LOG_VALUE.FromCamera;
            reduxStore.dispatch(changeUploadFileSubScreen(SUB_SCREEN.CAMERA_VIEW));
            GLOBAL_MEETING_DATA.uploadFileAction = UPLOAD_TYPE.FROM_CAMERA;
        } else if (uploadFileType === UPLOAD_TYPE.FROM_DEVICE) {
            firebaseLogValue = FIREBASE_LOG_VALUE.FromDevice;
            reduxStore.dispatch(changeUploadFileScreen(SELECTED_SCREEN.SELECT_FILES));
            GLOBAL_MEETING_DATA.uploadFileAction = UPLOAD_TYPE.FROM_DEVICE;
        } else {
            firebaseLogValue = FIREBASE_LOG_VALUE.FromUploadButton;
            GLOBAL_MEETING_DATA.uploadFileAction = UPLOAD_TYPE.EMPTY;
        }

        LogFirebase(FIREBASE_EVENT_CATEGORY.UploadFiles, FIREBASE_LOG_KEY.FileSelect, firebaseLogValue);
        LogFirebase(FIREBASE_EVENT_CATEGORY.UploadFiles, FIREBASE_LOG_KEY.SendMessage, FIREBASE_LOG_VALUE.UploadFileStartNotification);

        /* Send data track for upload file start */
        this.sendTrackOnUploadFileStartEnd(UPLOAD_FILE_DATA_TRACK_STATUS.START);
    };

    /*Function for upload File is enable or not */
    isUploadFilesView = () => {
        return reduxStore.getState().uploadFile.screenToShow !== SELECTED_SCREEN.OFF_VIEW;
    };

    getCompressionPercentage = function (imageSize) {
        if (this.isImageLargerThanAllowedSizeLimit(imageSize, IMAGE_COMPRESSION.WEB_MIN_SIZE)) {
            //image size is more than min size of limit, then compress it
            const compressionDivision = IMAGE_SIZE_KEYS.COMPRESSION_DIVISION;
            const compressionObj = IMAGE_COMPRESSION.FOR_WEB;
            const isAbove10MB = this.isImageLargerThanAllowedSizeLimit(imageSize, compressionDivision);
            const compression = COMPRESSION_TYPE[PublicVariables.featureFlag.IMAGECOMPRESSIONS];
            return compressionObj[isAbove10MB ? IMAGE_SIZE_KEYS.ABOVE_DIVISION : IMAGE_SIZE_KEYS.BELOW_DIVISION][compression].COMPRESSION;
        } else {
            //Do not compress it
            return 0;
        }
    };

    sendTrackOnUploadFileStartEnd = (status, thumbList = []) => {
        const msg = {
            notification_type: NOTIFICATION_TYPES.UPLOAD_FILE,
            device_type: GLOBAL_MEETING_DATA.device_type,
            status: status, // 1 - start, 2 - end, 3 - remainingcount
        };
        if (CALL_DATA.localIdentity) {
            msg.identity = CALL_DATA.localIdentity;
        }
        if (status === UPLOAD_FILE_DATA_TRACK_STATUS.END || status == UPLOAD_FILE_DATA_TRACK_STATUS.REMAINING_COUNT) {
            msg.remaining_count = GLOBAL_MEETING_DATA.remainingFileCount;
            LogFirebase(FIREBASE_EVENT_CATEGORY.UploadFiles, FIREBASE_LOG_KEY.UploadFileRemainingCount, { remainingFileCount: msg.remaining_count });
        }

        if (status === UPLOAD_FILE_DATA_TRACK_STATUS.END) {
            if (thumbList?.length === 0) {
                msg.thumbList = thumbList;
            } else {
                let currentChunk = [];
                let currentSize = 0;

                const sendChunk = (chunk) => {
                    msg.thumbList = chunk;
                    sendDataTrack(msg);
                };

                thumbList?.forEach((point) => {
                    const pointSize = new TextEncoder().encode(JSON.stringify(point)).length;
                    if (currentSize + pointSize > MAX_CHUNK_SIZE) {
                        // If adding the point exceeds the limit, send the current chunk and start a new one
                        sendChunk(currentChunk);
                        currentChunk = [];
                        currentSize = 0;
                    }
                    // Add the point to the current chunk and update the size
                    currentChunk.push(point);
                    currentSize += pointSize;
                });
                // Send the last chunk
                if (currentChunk.length > 0) {
                    sendChunk(currentChunk);
                }
            }
        }

        if (thumbList?.length === 0) {
            sendDataTrack(msg);
        }
    };

    onStartUploadFiles = (markupLiveStream) => {
        /* Hide zoom container */

        ////        if(markupLiveStream){
        ////            if ($("#collapseContainer")) {
        ////                $("#collapseContainer").hide();
        ////            }
        ////            if ($(".livePointerMainContainer")) {
        ////                $(".livePointerMainContainer").hide();
        ////            }
        ////        } else {
        ////            $("#videoScreenShareContainer").css("display", "none"); // hide video view
        ////        }

        if (dualStreamEnabled()) {
            ////        $('.gallery-pin-view').addClass("gallery-view-with-markup");
        }

        ////    chatService.closeChatModel();
    };

    onExitUploadFiles = (markupLiveStream) => {
        if (markupLiveStream) {
            /* this will be done at the time of live markup implementation */
        }
        const { expandView } = reduxStore.getState().commonReducer;
        if (expandView) {
            reduxStore.dispatch(setCurrentCallBodyScreen(CALL_BODY_SCREEN.VIDEO_SCREEN));
        } else if (dualStreamEnabled() && !expandView) {
            reduxStore.dispatch(setCurrentCallBodyScreen(CALL_BODY_SCREEN.GALLERY_VIEW));
        }
        if ((!dualStreamEnabled() && !CALL_DATA.myVideoON) || dualStreamEnabled()) {
            setTimeout(() => {
                const localContainer = expandView ? document.getElementById("expandedVideo") : getPublishVideoContainer();
                removeVideoElement(localContainer);
                setTimeout(() => {
                    attachVideoTracksStreamingIdentityVise();
                }, 200);
            }, 100);
        }
        if (dualStreamEnabled()) {
            /* apply this once dual stream implemented*/
            //// $('.gallery-pin-view').removeClass("gallery-view-with-markup");
        }
        zoomService.resetZoomLevelOnExitView(); // Reset a Zoom level on exit upload file for only web devices (in dual stream(expanded view))
    };

    handleUploadFileDataTrackMessage = (msg) => {
        if (!this.isUploadFilesView() && msg.status === UPLOAD_FILE_DATA_TRACK_STATUS.START) {
            // start upload file msg
            reduxStore.dispatch(setCurrentCallBodyScreen(CALL_BODY_SCREEN.NOTIFY_SCREEN)); // to disable other btns
            this.onStartUploadFiles(GLOBAL_MEETING_DATA.markupLiveStream);
        } else if (msg.status === UPLOAD_FILE_DATA_TRACK_STATUS.END) {
            // close upload file screen msg
            this.onExitUploadFiles(GLOBAL_MEETING_DATA.markupLiveStream);
            GLOBAL_MEETING_DATA.remainingFileCount = parseInt(msg.remaining_count);
            if (GLOBAL_MEETING_DATA.ucid) {
                if (msg.thumbList?.length > 0) {
                    msg.thumbList.forEach(function (img) {
                        takePhotoService.addThumbInList(
                            img.thumbURL,
                            img.image_url,
                            img.imageName,
                            img.caption,
                            UPLOAD_TYPE_PARAM_FOR_UPLOAD_FILES.toString()
                        );
                    });
                }
            }
        } else if (!this.isUploadFilesView() && msg.status === UPLOAD_FILE_DATA_TRACK_STATUS.REMAINING_COUNT) {
            // remaining file count msg on delete files
            GLOBAL_MEETING_DATA.remainingFileCount = parseInt(msg.remaining_count);
        }
    };

    resetFlags = () => {
        this.isSizeExceed = false;
        UPLOAD_FILES_DATA.selectedImageIndex = -1;
        UPLOAD_FILES_DATA.selectedImageObj = null;

        reduxStore.dispatch(updateImagesArray([]));
        reduxStore.dispatch(setUploadFileContainerImageArray([]));
        reduxStore.dispatch(changeUploadFileScreen(SELECTED_SCREEN.OFF_VIEW));
        reduxStore.dispatch(changePreviousScreen(SELECTED_SCREEN.OFF_VIEW));
        reduxStore.dispatch(changeUploadFileSubScreen(SUB_SCREEN.OFF_VIEW));
        reduxStore.dispatch(changeIsFromMarkupValue(false));
        reduxStore.dispatch(setUplodedFilesThumbListArray([]));
        reduxStore.dispatch(setIsHideFinalAddBtn(false));
    };
}

export default new UploadFileService();
